import React, { useCallback, useMemo, useState } from 'react';
import { select } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { pick } from 'underscore';

import { ServiceTypeFilter } from '@rover/components/src/formFields';
import { ServiceFilterType } from '@rover/components/src/formFields/ServiceTypeFilter/ServiceTypeFilter';
import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { LOCATION_DEFAULT_TITLE_2 } from '@rover/constants/src/location/location.constants';
import { Box, Button, Flex } from '@rover/kibble/core';
import {
  emitSearchFormCtaAnalytics,
  SelectDates,
  UpdateLocation,
  UpdateServiceType,
} from '@rover/react-lib/src/pages/landing/utilities/analyticsEvents';
import useDates from '@rover/react-lib/src/pages/landing/utilities/useDates';
import { Location } from '@rover/react-lib/src/pages/search/SearchPage/components';
import type { PaginatedServiceTypeList } from '@rover/rsdk/src/apiClient/latest/model/paginatedServiceTypeList';
import type { ServiceType } from '@rover/rsdk/src/apiClient/latest/model/serviceType';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import FilterLabel from '@rover/shared/js/search/components/FilterLabel';
import { PetType } from '@rover/types/src/Pet';
import type { SearchFilters } from '@rover/types/src/SearchFilters';
import type { ServiceTypeSlug } from '@rover/types/src/ServiceType';

import getSearchUrl from '../../../utils/getSearchUrl';
import { SERVICE_SEARCH_FORM_SUBMIT_TYPES } from '../ServiceLanding/constants';

import DateRangePicker from './DateRangePicker';

export type LandingSearchFormFilters = Partial<SearchFilters> &
  Pick<SearchFilters, 'location' | 'petType' | 'serviceType'> & {
    puppy?: boolean;
  };

type SubmitButtonTextVariant =
  (typeof SERVICE_SEARCH_FORM_SUBMIT_TYPES)[keyof typeof SERVICE_SEARCH_FORM_SUBMIT_TYPES];

export type Props = {
  locale: Locale;
  serviceTypes: PaginatedServiceTypeList;
  /**
   * `slug` is the backend-derived localized service-type slug — see `InputEventParameters` in
   * analyticsEvents.ts. `startDateSelectorDescription`/`endDateSelectorDescription` are already
   * localized by the API for this service type; omit them (most service types don't set these)
   * to fall back to the generic "Start"/"End" labels.
   */
  internalServiceType: Pick<ServiceType, 'slug'> &
    Partial<Pick<ServiceType, 'startDateSelectorDescription' | 'endDateSelectorDescription'>>;
  initialSearchFilters: LandingSearchFormFilters;
  showSearchFilters?: boolean;
  submitButtonTextVariant?: SubmitButtonTextVariant;
};

const LandingSearchForm: React.FC<Props> = ({
  locale,
  serviceTypes,
  internalServiceType,
  initialSearchFilters,
  showSearchFilters = true,
  submitButtonTextVariant = 'search',
}) => {
  const {
    slug: internalServiceTypeSlug,
    startDateSelectorDescription,
    endDateSelectorDescription,
  } = internalServiceType;

  const { t, i18n } = useLingui();
  const { startDate, endDate, setStartDate, setEndDate } = useDates();

  const [searchFilters, setSearchFilters] = useState<LandingSearchFormFilters>({
    ...initialSearchFilters,
  });

  const defaultLocationFilters = useMemo(
    () =>
      pick(initialSearchFilters, [
        'centerlat',
        'centerlng',
        'location',
        'locationAccuracy',
        'locationType',
        'rawLocationTypes',
      ]),
    [initialSearchFilters]
  );

  const onLocationClear = useCallback(() => {
    const newFilters = { ...searchFilters, ...defaultLocationFilters };
    setSearchFilters(newFilters);
  }, [searchFilters, defaultLocationFilters]);

  const onLocationChange = useCallback<React.ComponentProps<typeof Location>['onChange']>(
    (newPartialFilters) => {
      // The page's seoLocationSlug encodes this city; once the user picks a
      // different location it no longer describes the search, so drop it.
      const newFilters = { ...searchFilters, seoLocationSlug: undefined };
      Object.entries(newPartialFilters).forEach(([key, value]) => {
        newFilters[key] = value || searchFilters[key];
      });
      setSearchFilters(newFilters);
      emitAnalyticsEvent(new UpdateLocation({}));
    },
    [searchFilters]
  );

  const onServiceTypeChange = useCallback<
    React.ComponentProps<typeof ServiceTypeFilter>['onChange']
  >(
    (newPartialFilters) => {
      // The page's seoLocationSlug also encodes its service type; drop it once
      // the user switches services so search doesn't resolve the wrong page.
      const newFilters = { ...searchFilters, seoLocationSlug: undefined };
      Object.entries(newPartialFilters).forEach(([key, value]) => {
        newFilters[key] = value || searchFilters[key];
      });
      setSearchFilters(newFilters);
      emitAnalyticsEvent(
        new UpdateServiceType({ selectedServiceType: newPartialFilters.serviceType })
      );
    },
    [searchFilters]
  );

  const searchLink = useMemo(() => {
    return getSearchUrl(
      { ...searchFilters, startDate, endDate },
      locale,
      serviceTypes.results?.find((s) => s.slug === searchFilters.serviceType),
      true
    );
  }, [serviceTypes.results, searchFilters, locale, startDate, endDate]);

  const serviceTypeOptions = useMemo(
    () =>
      (serviceTypes.results ?? [])
        .filter(
          (s) =>
            s.browsable &&
            (s.suggestedPetType === 'any' ||
              initialSearchFilters.petType.includes(s.suggestedPetType as PetType))
        )
        .map((s) => ({
          title: s.displayName,
          subtitle: s.description,
          value: s.slug as ServiceTypeSlug,
        })),
    [initialSearchFilters, serviceTypes.results]
  );

  return (
    <Flex flexDirection="column">
      {showSearchFilters && (
        <>
          <ServiceTypeFilter
            label={t`Explore other services`}
            filterType={ServiceFilterType.ICON_DROPDOWN}
            onChange={onServiceTypeChange}
            {...{ serviceTypeOptions, searchFilters }}
          />
          <Box my={['4x', '6x']}>
            <Location
              label={i18n._(LOCATION_DEFAULT_TITLE_2)}
              onChange={onLocationChange}
              onClear={onLocationClear}
              locationInputId="landing-search-form-location"
            />
          </Box>
          <FilterLabel>{t`Which dates do you need?`}</FilterLabel>
          <DateRangePicker
            {...{
              startPlaceholder: startDateSelectorDescription || t`Start`,
              endPlaceholder: endDateSelectorDescription || t`End`,
              endDate,
              startDate,
              setStartDate,
              setEndDate,
              serviceType: searchFilters.serviceType,
              locale,
              onChange: (dateRange) => {
                dateRange.startDate &&
                  // only fire on the initial startDate change to avoid double tracking
                  !dateRange.endDate &&
                  emitAnalyticsEvent(
                    new SelectDates({
                      internalServiceTypeSlug,
                    })
                  );
              },
              showArrow: false,
              inline: true,
            }}
          />
        </>
      )}
      <Button
        variant="primary"
        size="large"
        fullWidth
        mt="4x"
        href={searchLink}
        onPress={(): void => {
          emitSearchFormCtaAnalytics({
            internalServiceTypeSlug,
          });
        }}
      >
        {t({
          message: select(submitButtonTextVariant, {
            search: 'Search',
            dogBoarding: 'Find trusted dog boarders',
            petSitters: 'Find trusted pet sitters',
            houseSitters: 'Find trusted house sitters',
            dayCare: 'Find trusted day care',
            dogWalkers: 'Find trusted dog walkers',
            other: 'Search',
          }),
        })}
      </Button>
    </Flex>
  );
};

export default LandingSearchForm;
